import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../core/store/store';
import { getApiBaseUrl } from '../../../../core/config/api';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Balance {
  argent: number;
  medailles: number;
  updated_at: string;
}

interface CagnotteLog {
  id: number;
  type: 'contribution_joueur' | 'ajout_cm' | 'retrait_cm' | 'evenement_creation' | 'evenement_annule';
  argent_delta: number;
  medailles_delta: number;
  source_username: string | null;
  cm_username: string | null;
  event_id: number | null;
  description: string;
  argent_avant: number;
  argent_apres: number;
  medailles_avant: number;
  medailles_apres: number;
  created_at: string;
}

interface TeamEvent {
  id: number;
  titre: string;
  description: string | null;
  argent_cout: number;
  medailles_cout: number;
  statut: 'planifie' | 'en_cours' | 'termine' | 'annule';
  date_evenement: string | null;
  created_by_username: string | null;
  created_at: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const isCM = (r: number) => r === 1;

const LOG_TYPE_LABELS: Record<string, { label: string; color: string; sign: string }> = {
  contribution_joueur: { label: 'Contribution', color: 'text-emerald-400', sign: '+' },
  ajout_cm:           { label: 'Ajout CM',      color: 'text-sky-400',     sign: '+' },
  retrait_cm:         { label: 'Retrait CM',    color: 'text-red-400',     sign: '−' },
  evenement_creation: { label: 'Événement',     color: 'text-amber-400',   sign: '−' },
  evenement_annule:   { label: 'Remboursement', color: 'text-emerald-400', sign: '+' },
};

const EVENT_STATUT_LABELS: Record<string, { label: string; color: string; bg: string; border: string }> = {
  planifie:  { label: 'Planifié',   color: 'text-sky-300',     bg: 'bg-sky-500/10',     border: 'border-sky-500/30' },
  en_cours:  { label: 'En cours',   color: 'text-amber-300',   bg: 'bg-amber-500/10',   border: 'border-amber-500/30' },
  termine:   { label: 'Terminé',    color: 'text-emerald-300', bg: 'bg-emerald-500/10', border: 'border-emerald-500/30' },
  annule:    { label: 'Annulé',     color: 'text-zinc-500',    bg: 'bg-zinc-800/50',    border: 'border-zinc-700/50' },
};

function fmt(n: number) { return Number(n).toLocaleString('fr-FR'); }
function fmtDate(s: string) { return new Date(s).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }

/** Headers d'authentification (le middleware serveur exige le token Bearer, pas le cookie). */
function authHeaders(json = false): HeadersInit {
  const token = localStorage.getItem('token');
  return {
    ...(json ? { 'Content-Type': 'application/json' } : {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

// ─── Composant principal ──────────────────────────────────────────────────────

const PanelCagnotteV1: React.FC = () => {
  const currentUser = useSelector((state: RootState) => state.user.currentUser);
  const [balance, setBalance] = useState<Balance | null>(null);
  const [logs, setLogs] = useState<CagnotteLog[]>([]);
  const [events, setEvents] = useState<TeamEvent[]>([]);
  const [logsPage, setLogsPage] = useState(1);
  const [logsTotal, setLogsTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'overview' | 'logs' | 'events'>('overview');

  // Modales
  const [showAddModal, setShowAddModal] = useState(false);
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [showEventModal, setShowEventModal] = useState<TeamEvent | null | 'new'>(null);

  const cm = isCM(currentUser?.roles_id ?? 99);

  const fetchOverview = useCallback(async () => {
    try {
      const res = await fetch(`${getApiBaseUrl(true)}/panel/v1/cagnotte`, { credentials: 'include', headers: authHeaders() });
      const data = await res.json();
      if (data.success) {
        setBalance(data.data.balance);
        setEvents(data.data.events);
      }
    } catch { /* noop */ }
    finally { setLoading(false); }
  }, []);

  const fetchLogs = useCallback(async (page: number) => {
    try {
      const res = await fetch(`${getApiBaseUrl(true)}/panel/v1/cagnotte/logs?page=${page}&limit=50`, { credentials: 'include', headers: authHeaders() });
      const data = await res.json();
      if (data.success) {
        setLogs(data.data.logs);
        setLogsTotal(data.data.pagination.total);
      }
    } catch { /* noop */ }
  }, []);

  useEffect(() => { fetchOverview(); }, [fetchOverview]);
  useEffect(() => {
    if (activeTab === 'logs') fetchLogs(logsPage);
  }, [activeTab, logsPage, fetchLogs]);

  if (loading) {
    return <div className="p-8 text-zinc-500 text-sm">Chargement…</div>;
  }

  return (
    <div className="flex flex-col gap-6 p-6 max-w-5xl mx-auto rounded-2xl border border-white/5 bg-[#070c1a]/70 backdrop-blur-sm">

      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-xl font-bold text-white">Cagnotte & Événements Team</h1>
          <p className="text-xs text-zinc-500 mt-1">
            Alimentée automatiquement par les dépenses des joueurs (argent + médailles).
            {cm ? ' En tant que CM, vous pouvez gérer les fonds et créer des événements.' : ' Vue lecture seule.'}
          </p>
        </div>
        {cm && (
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={() => setShowAddModal(true)}
              className="px-3 py-1.5 rounded-lg bg-emerald-600/20 border border-emerald-500/40 text-emerald-300 text-xs font-semibold hover:bg-emerald-600/35 transition-colors"
            >
              + Ajouter des fonds
            </button>
            <button
              onClick={() => setShowRemoveModal(true)}
              className="px-3 py-1.5 rounded-lg bg-red-600/20 border border-red-500/40 text-red-300 text-xs font-semibold hover:bg-red-600/35 transition-colors"
            >
              − Retirer des fonds
            </button>
            <button
              onClick={() => setShowEventModal('new')}
              className="px-3 py-1.5 rounded-lg bg-amber-600/20 border border-amber-500/40 text-amber-300 text-xs font-semibold hover:bg-amber-600/35 transition-colors"
            >
              🗓 Créer un événement
            </button>
          </div>
        )}
      </div>

      {/* Solde */}
      <div className="grid grid-cols-2 gap-4">
        <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-5">
          <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Solde argent</p>
          <p className="text-3xl font-bold text-emerald-300">{fmt(balance?.argent ?? 0)} €</p>
          <p className="text-[10px] text-zinc-600 mt-1">
            Dernière mise à jour : {balance?.updated_at ? fmtDate(balance.updated_at) : '—'}
          </p>
        </div>
        <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-5">
          <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Solde médailles</p>
          <p className="text-3xl font-bold text-amber-300">{fmt(balance?.medailles ?? 0)} 🏅</p>
          <p className="text-[10px] text-zinc-600 mt-1">Cumulées depuis les dépenses joueurs</p>
        </div>
      </div>

      {/* Onglets */}
      <div className="flex gap-1 border-b border-zinc-800/60 pb-0">
        {(['overview', 'logs', 'events'] as const).map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 text-xs font-semibold rounded-t-lg border-b-2 transition-colors ${
              activeTab === tab
                ? 'text-white border-sky-500'
                : 'text-zinc-500 border-transparent hover:text-zinc-300'
            }`}
          >
            {tab === 'overview' ? 'Aperçu' : tab === 'logs' ? 'Journal' : 'Événements'}
          </button>
        ))}
      </div>

      {/* Contenu onglets */}
      {activeTab === 'overview' && (
        <OverviewTab events={events} cm={cm} balance={balance} onEditEvent={cm ? setShowEventModal : undefined} />
      )}
      {activeTab === 'logs' && (
        <LogsTab
          logs={logs}
          total={logsTotal}
          page={logsPage}
          onPageChange={(p) => { setLogsPage(p); }}
        />
      )}
      {activeTab === 'events' && (
        <EventsTab events={events} cm={cm} onEditEvent={cm ? setShowEventModal : undefined} />
      )}

      {/* Modales */}
      {showAddModal && (
        <FundsModal
          mode="add"
          onClose={() => setShowAddModal(false)}
          onSuccess={() => { setShowAddModal(false); fetchOverview(); }}
        />
      )}
      {showRemoveModal && (
        <FundsModal
          mode="remove"
          balance={balance}
          onClose={() => setShowRemoveModal(false)}
          onSuccess={() => { setShowRemoveModal(false); fetchOverview(); }}
        />
      )}
      {showEventModal !== null && (
        <EventModal
          existing={showEventModal === 'new' ? null : showEventModal}
          balance={balance}
          onClose={() => setShowEventModal(null)}
          onSuccess={() => { setShowEventModal(null); fetchOverview(); }}
        />
      )}
    </div>
  );
};

// ─── Onglet Aperçu ────────────────────────────────────────────────────────────

const OverviewTab: React.FC<{
  events: TeamEvent[];
  balance: Balance | null;
  cm: boolean;
  onEditEvent?: (e: TeamEvent) => void;
}> = ({ events, cm, onEditEvent }) => {
  const upcoming = events.filter((e) => e.statut === 'planifie' || e.statut === 'en_cours');
  return (
    <div className="space-y-4">
      <p className="text-xs text-zinc-500">
        {upcoming.length === 0
          ? 'Aucun événement planifié pour le moment.'
          : `${upcoming.length} événement(s) à venir ou en cours.`}
      </p>
      {upcoming.map((ev) => <EventCard key={ev.id} event={ev} cm={cm} onEdit={onEditEvent} />)}
    </div>
  );
};

// ─── Onglet Journal ───────────────────────────────────────────────────────────

const LogsTab: React.FC<{
  logs: CagnotteLog[];
  total: number;
  page: number;
  onPageChange: (p: number) => void;
}> = ({ logs, total, page, onPageChange }) => {
  const pages = Math.ceil(total / 50);
  return (
    <div className="space-y-3">
      <p className="text-xs text-zinc-500">{total} opération(s) au total</p>
      <div className="rounded-xl border border-zinc-800 overflow-hidden">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-zinc-900/80 text-zinc-500 uppercase tracking-wider text-[10px]">
              <th className="px-3 py-2.5 text-left">Type</th>
              <th className="px-3 py-2.5 text-left">Description</th>
              <th className="px-3 py-2.5 text-right">Argent</th>
              <th className="px-3 py-2.5 text-right">Médailles</th>
              <th className="px-3 py-2.5 text-right">Solde après</th>
              <th className="px-3 py-2.5 text-right">Date</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-800/50">
            {logs.length === 0 && (
              <tr><td colSpan={6} className="px-3 py-6 text-center text-zinc-600">Aucune opération</td></tr>
            )}
            {logs.map((log) => {
              const t = LOG_TYPE_LABELS[log.type] ?? { label: log.type, color: 'text-zinc-400', sign: '' };
              const argPos = log.argent_delta >= 0;
              return (
                <tr key={log.id} className="hover:bg-zinc-900/30 transition-colors">
                  <td className="px-3 py-2">
                    <span className={`text-[10px] font-semibold ${t.color}`}>{t.label}</span>
                    <div className="text-[10px] text-zinc-600">
                      {log.source_username ? `par ${log.source_username}` : log.cm_username ? `CM: ${log.cm_username}` : ''}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-zinc-400 max-w-[200px] truncate">{log.description}</td>
                  <td className={`px-3 py-2 text-right font-mono font-semibold ${argPos ? 'text-emerald-400' : 'text-red-400'}`}>
                    {log.argent_delta !== 0 ? `${argPos ? '+' : ''}${fmt(log.argent_delta)} €` : '—'}
                  </td>
                  <td className={`px-3 py-2 text-right font-mono font-semibold ${log.medailles_delta >= 0 ? 'text-amber-400' : 'text-red-400'}`}>
                    {log.medailles_delta !== 0 ? `${log.medailles_delta >= 0 ? '+' : ''}${fmt(log.medailles_delta)}` : '—'}
                  </td>
                  <td className="px-3 py-2 text-right text-zinc-400 font-mono text-[10px]">
                    {fmt(log.argent_apres)} €<br />
                    <span className="text-amber-500/70">{fmt(log.medailles_apres)} 🏅</span>
                  </td>
                  <td className="px-3 py-2 text-right text-zinc-600 text-[10px] whitespace-nowrap">{fmtDate(log.created_at)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {pages > 1 && (
        <div className="flex justify-center gap-2">
          <button disabled={page <= 1} onClick={() => onPageChange(page - 1)} className="px-3 py-1.5 rounded-lg bg-zinc-800 text-zinc-300 text-xs disabled:opacity-40">← Précédent</button>
          <span className="px-3 py-1.5 text-xs text-zinc-500">{page} / {pages}</span>
          <button disabled={page >= pages} onClick={() => onPageChange(page + 1)} className="px-3 py-1.5 rounded-lg bg-zinc-800 text-zinc-300 text-xs disabled:opacity-40">Suivant →</button>
        </div>
      )}
    </div>
  );
};

// ─── Onglet Événements ────────────────────────────────────────────────────────

const EventsTab: React.FC<{
  events: TeamEvent[];
  cm: boolean;
  onEditEvent?: (e: TeamEvent) => void;
}> = ({ events, cm, onEditEvent }) => (
  <div className="space-y-3">
    {events.length === 0 && <p className="text-xs text-zinc-500">Aucun événement créé.</p>}
    {events.map((ev) => <EventCard key={ev.id} event={ev} cm={cm} onEdit={onEditEvent} />)}
  </div>
);

// ─── Carte événement ──────────────────────────────────────────────────────────

const EventCard: React.FC<{
  event: TeamEvent;
  cm: boolean;
  onEdit?: (e: TeamEvent) => void;
}> = ({ event: ev, cm, onEdit }) => {
  const s = EVENT_STATUT_LABELS[ev.statut] ?? EVENT_STATUT_LABELS.planifie;
  return (
    <div className={`rounded-xl border ${s.border} ${s.bg} p-4 flex items-start gap-4`}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1 flex-wrap">
          <span className={`text-[10px] font-bold uppercase tracking-wider ${s.color} border ${s.border} px-2 py-0.5 rounded-full`}>
            {s.label}
          </span>
          <h3 className="text-sm font-bold text-white">{ev.titre}</h3>
        </div>
        {ev.description && <p className="text-xs text-zinc-400 mb-2 leading-relaxed">{ev.description}</p>}
        <div className="flex flex-wrap gap-3 text-[10px] text-zinc-500">
          {ev.date_evenement && <span>📅 {fmtDate(ev.date_evenement)}</span>}
          {ev.argent_cout > 0 && <span className="text-amber-500">💰 {fmt(ev.argent_cout)} €</span>}
          {ev.medailles_cout > 0 && <span className="text-amber-400">🏅 {fmt(ev.medailles_cout)}</span>}
          {ev.created_by_username && <span>Créé par {ev.created_by_username}</span>}
          <span>le {fmtDate(ev.created_at)}</span>
        </div>
      </div>
      {cm && onEdit && ev.statut !== 'termine' && ev.statut !== 'annule' && (
        <button
          onClick={() => onEdit(ev)}
          className="shrink-0 px-3 py-1.5 rounded-lg bg-zinc-800 border border-zinc-700 text-zinc-300 text-xs font-semibold hover:bg-zinc-700 transition-colors"
        >
          Modifier
        </button>
      )}
    </div>
  );
};

// ─── Modal ajout/retrait ──────────────────────────────────────────────────────

const FundsModal: React.FC<{
  mode: 'add' | 'remove';
  balance?: Balance | null;
  onClose: () => void;
  onSuccess: () => void;
}> = ({ mode, balance, onClose, onSuccess }) => {
  const [argent, setArgent] = useState('');
  const [medailles, setMedailles] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const a = Math.max(0, Number(argent) || 0);
    const m = Math.max(0, Number(medailles) || 0);
    if (a === 0 && m === 0) { setError('Entrez au moins une valeur.'); return; }

    setLoading(true);
    try {
      const res = await fetch(`${getApiBaseUrl(true)}/panel/v1/cagnotte/${mode === 'add' ? 'add' : 'remove'}`, {
        method: 'POST',
        credentials: 'include',
        headers: authHeaders(true),
        body: JSON.stringify({ argent: a, medailles: m, description: description.trim() }),
      });
      const data = await res.json();
      if (!data.success) { setError(data.error); return; }
      onSuccess();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const isAdd = mode === 'add';
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="w-full max-w-sm bg-zinc-900 border border-zinc-700 rounded-2xl shadow-2xl p-6">
        <h2 className={`text-base font-bold mb-4 ${isAdd ? 'text-emerald-300' : 'text-red-300'}`}>
          {isAdd ? '+ Ajouter des fonds' : '− Retirer des fonds'}
        </h2>
        {!isAdd && balance && (
          <div className="mb-4 p-3 rounded-lg bg-zinc-800 border border-zinc-700 text-xs text-zinc-400">
            Solde disponible : <span className="text-emerald-300 font-semibold">{fmt(balance.argent)} €</span>
            {' / '}<span className="text-amber-300 font-semibold">{fmt(balance.medailles)} 🏅</span>
          </div>
        )}
        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label className="text-[10px] text-zinc-500 uppercase tracking-wider block mb-1">Argent (€)</label>
            <input
              type="number" min="0" value={argent}
              onChange={(e) => setArgent(e.target.value)}
              className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-zinc-500"
              placeholder="0"
            />
          </div>
          <div>
            <label className="text-[10px] text-zinc-500 uppercase tracking-wider block mb-1">Médailles</label>
            <input
              type="number" min="0" value={medailles}
              onChange={(e) => setMedailles(e.target.value)}
              className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-zinc-500"
              placeholder="0"
            />
          </div>
          <div>
            <label className="text-[10px] text-zinc-500 uppercase tracking-wider block mb-1">Motif (facultatif)</label>
            <input
              type="text" value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={255}
              className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-zinc-500"
              placeholder="Ex : Don équipe, correction solde…"
            />
          </div>
          {error && <p className="text-xs text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">{error}</p>}
          <div className="flex gap-2 pt-1">
            <button type="button" onClick={onClose} className="flex-1 py-2 rounded-xl bg-zinc-800 text-zinc-300 text-sm font-semibold hover:bg-zinc-700 transition-colors">Annuler</button>
            <button type="submit" disabled={loading} className={`flex-1 py-2 rounded-xl text-sm font-bold transition-colors disabled:opacity-50 ${isAdd ? 'bg-emerald-600/30 text-emerald-200 border border-emerald-500/40 hover:bg-emerald-600/50' : 'bg-red-600/30 text-red-200 border border-red-500/40 hover:bg-red-600/50'}`}>
              {loading ? 'En cours…' : isAdd ? 'Ajouter' : 'Retirer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─── Modal événement ──────────────────────────────────────────────────────────

const EventModal: React.FC<{
  existing: TeamEvent | null;
  balance: Balance | null;
  onClose: () => void;
  onSuccess: () => void;
}> = ({ existing, balance, onClose, onSuccess }) => {
  const isNew = existing === null;
  const [titre, setTitre] = useState(existing?.titre ?? '');
  const [description, setDescription] = useState(existing?.description ?? '');
  const [argentCout, setArgentCout] = useState(String(existing?.argent_cout ?? ''));
  const [medaillesCout, setMedaillesCout] = useState(String(existing?.medailles_cout ?? ''));
  const [dateEv, setDateEv] = useState(
    existing?.date_evenement ? existing.date_evenement.slice(0, 16) : ''
  );
  const [statut, setStatut] = useState<'planifie' | 'en_cours' | 'termine' | 'annule'>(
    existing?.statut ?? 'planifie'
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!titre.trim()) { setError('Le titre est obligatoire.'); return; }

    setLoading(true);
    try {
      const url = isNew
        ? `${getApiBaseUrl(true)}/panel/v1/cagnotte/events`
        : `${getApiBaseUrl(true)}/panel/v1/cagnotte/events/${existing!.id}`;
      const method = isNew ? 'POST' : 'PATCH';
      const body = isNew
        ? { titre: titre.trim(), description: description.trim() || null, argent_cout: Number(argentCout) || 0, medailles_cout: Number(medaillesCout) || 0, date_evenement: dateEv || null }
        : { titre: titre.trim(), description: description.trim() || null, statut, date_evenement: dateEv || null };

      const res = await fetch(url, {
        method, credentials: 'include',
        headers: authHeaders(true),
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!data.success) { setError(data.error); return; }
      onSuccess();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="w-full max-w-md bg-zinc-900 border border-zinc-700 rounded-2xl shadow-2xl p-6">
        <h2 className="text-base font-bold text-amber-300 mb-4">
          {isNew ? '🗓 Créer un événement' : `Modifier — ${existing?.titre}`}
        </h2>
        {isNew && balance && (
          <div className="mb-4 p-3 rounded-lg bg-zinc-800 border border-zinc-700 text-xs text-zinc-400">
            Fonds disponibles : <span className="text-emerald-300 font-semibold">{fmt(balance.argent)} €</span>
            {' / '}<span className="text-amber-300 font-semibold">{fmt(balance.medailles)} 🏅</span>
          </div>
        )}
        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label className="text-[10px] text-zinc-500 uppercase tracking-wider block mb-1">Titre *</label>
            <input
              type="text" value={titre} onChange={(e) => setTitre(e.target.value)} maxLength={100} required
              className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-zinc-500"
              placeholder="Nom de l'événement"
            />
          </div>
          <div>
            <label className="text-[10px] text-zinc-500 uppercase tracking-wider block mb-1">Description</label>
            <textarea
              value={description} onChange={(e) => setDescription(e.target.value)} rows={3}
              className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-zinc-500 resize-none"
              placeholder="Détails de l'événement…"
            />
          </div>
          <div>
            <label className="text-[10px] text-zinc-500 uppercase tracking-wider block mb-1">Date de l'événement</label>
            <input
              type="datetime-local" value={dateEv} onChange={(e) => setDateEv(e.target.value)}
              className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-zinc-500"
            />
          </div>
          {isNew && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] text-zinc-500 uppercase tracking-wider block mb-1">Coût argent (€)</label>
                <input
                  type="number" min="0" value={argentCout} onChange={(e) => setArgentCout(e.target.value)}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-zinc-500"
                  placeholder="0"
                />
              </div>
              <div>
                <label className="text-[10px] text-zinc-500 uppercase tracking-wider block mb-1">Coût médailles</label>
                <input
                  type="number" min="0" value={medaillesCout} onChange={(e) => setMedaillesCout(e.target.value)}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-zinc-500"
                  placeholder="0"
                />
              </div>
            </div>
          )}
          {!isNew && (
            <div>
              <label className="text-[10px] text-zinc-500 uppercase tracking-wider block mb-1">Statut</label>
              <select
                value={statut}
                onChange={(e) => setStatut(e.target.value as typeof statut)}
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-zinc-500"
              >
                {(['planifie', 'en_cours', 'termine', 'annule'] as const).map((s) => (
                  <option key={s} value={s}>{EVENT_STATUT_LABELS[s].label}</option>
                ))}
              </select>
              {statut === 'annule' && (
                <p className="text-[10px] text-amber-400 mt-1">
                  ⚠ L'annulation remboursera les fonds dans la cagnotte.
                </p>
              )}
            </div>
          )}
          {error && <p className="text-xs text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">{error}</p>}
          <div className="flex gap-2 pt-1">
            <button type="button" onClick={onClose} className="flex-1 py-2 rounded-xl bg-zinc-800 text-zinc-300 text-sm font-semibold hover:bg-zinc-700 transition-colors">Annuler</button>
            <button type="submit" disabled={loading} className="flex-1 py-2 rounded-xl bg-amber-600/30 text-amber-200 border border-amber-500/40 text-sm font-bold hover:bg-amber-600/50 transition-colors disabled:opacity-50">
              {loading ? 'En cours…' : isNew ? 'Créer l\'événement' : 'Enregistrer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PanelCagnotteV1;
